import React from "react";
import { ChevronLeft, ChevronRight, AlertCircle, CheckCircle2 } from "lucide-react";
import {
  MaintenanceAreaDto,
  MaintenanceTaskDto,
  maintenanceAreasApi,
  maintenanceTasksApi,
} from "../../../services/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CompleteTaskDialog } from "../tasks/CompleteTaskDialog";
import { INTERVAL_UNITS, IntervalUnit, RECURRENCE_TYPES } from "../../../services/api";
import {
  CATEGORY_LABELS, PRIORITY_LABELS, formatDate, isOverdue,
  intToCategory, intToPriority,
} from "../utils";

const MONTH_LABELS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const WEEKDAY_LABELS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const PRIORITY_COLORS: Record<string, string> = {
  Critical: "bg-red-100 text-red-800 border-red-300",
  High: "bg-orange-100 text-orange-800 border-orange-300",
  Normal: "bg-sky-100 text-sky-800 border-sky-300",
  Low: "bg-slate-100 text-slate-700 border-slate-300",
};

function localDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addInterval(date: Date, value: number, unit: IntervalUnit): Date {
  const d = new Date(date.getTime());
  switch (unit) {
    case "Days": d.setDate(d.getDate() + value); break;
    case "Weeks": d.setDate(d.getDate() + value * 7); break;
    case "Months": d.setMonth(d.getMonth() + value); break;
    case "Years": d.setFullYear(d.getFullYear() + value); break;
  }
  return d;
}

const ONE_TIME = RECURRENCE_TYPES.indexOf("OneTime");

// Generates every occurrence of a task whose date falls within [from, to].
// For recurrent tasks, projects forward from nextDueAt; safety-capped to avoid runaway loops.
function expandOccurrences(task: MaintenanceTaskDto, from: Date, to: Date): Date[] {
  const first = new Date(task.nextDueAt);
  const occurrences: Date[] = [];
  if (task.recurrenceType === ONE_TIME) {
    if (first >= from && first <= to) occurrences.push(first);
    return occurrences;
  }
  if (task.intervalValue == null || task.intervalUnit == null || task.intervalValue <= 0) {
    if (first >= from && first <= to) occurrences.push(first);
    return occurrences;
  }
  const unit = INTERVAL_UNITS[task.intervalUnit];
  if (!unit) return occurrences;
  let current = first;
  for (let i = 0; i < 500 && current <= to; i += 1) {
    if (current >= from) occurrences.push(current);
    current = addInterval(current, task.intervalValue, unit);
  }
  return occurrences;
}

function startOfMonth(year: number, month: number): Date {
  return new Date(year, month, 1);
}

// Days to show in the grid: from the Monday on or before day 1 to the Sunday on or after the last day.
function buildMonthGrid(year: number, month: number): Date[] {
  const first = startOfMonth(year, month);
  // getDay: 0=Sunday..6=Saturday. We want Monday-first: shift so Monday=0.
  const shift = (first.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - shift);
  const days: Date[] = [];
  for (let i = 0; i < 42; i += 1) {
    days.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }
  return days;
}

export const CalendarPage: React.FC = () => {
  const today = React.useMemo(() => new Date(), []);
  const [year, setYear] = React.useState(today.getFullYear());
  const [month, setMonth] = React.useState(today.getMonth());

  const [areas, setAreas] = React.useState<MaintenanceAreaDto[]>([]);
  const [tasks, setTasks] = React.useState<MaintenanceTaskDto[]>([]);
  const [filterAreaId, setFilterAreaId] = React.useState<string>("");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [completing, setCompleting] = React.useState<MaintenanceTaskDto | null>(null);
  const [selectedDayKey, setSelectedDayKey] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [a, t] = await Promise.all([
        maintenanceAreasApi.getAll(false),
        maintenanceTasksApi.search({
          areaId: filterAreaId || undefined,
        }),
      ]);
      setAreas(a);
      setTasks(t);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [filterAreaId]);

  React.useEffect(() => { void reload(); }, [reload]);

  const days = React.useMemo(() => buildMonthGrid(year, month), [year, month]);

  // Index task occurrences by local date key, projecting recurrent tasks across the visible grid.
  const tasksByDay = React.useMemo(() => {
    const map = new Map<string, MaintenanceTaskDto[]>();
    if (days.length === 0) return map;
    const from = days[0];
    const last = days[days.length - 1];
    const to = new Date(last.getFullYear(), last.getMonth(), last.getDate(), 23, 59, 59, 999);
    for (const t of tasks) {
      if (!t.isActive) continue;
      const occurrences = expandOccurrences(t, from, to);
      for (const occ of occurrences) {
        const key = localDateKey(occ);
        const list = map.get(key);
        if (list) list.push(t); else map.set(key, [t]);
      }
    }
    return map;
  }, [tasks, days]);

  const overdueTasks = React.useMemo(
    () => tasks.filter((t) => isOverdue(t)).sort((a, b) =>
      new Date(a.nextDueAt).getTime() - new Date(b.nextDueAt).getTime()
    ),
    [tasks],
  );

  const todayKey = localDateKey(today);

  const goPrev = () => {
    if (month === 0) { setMonth(11); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const goNext = () => {
    if (month === 11) { setMonth(0); setYear(year + 1); }
    else setMonth(month + 1);
  };
  const goToday = () => {
    setMonth(today.getMonth());
    setYear(today.getFullYear());
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:flex-wrap gap-3 md:items-end">
        <div className="w-full md:w-56">
          <label className="text-xs text-slate-500">Área</label>
          <Select
            value={filterAreaId || "_all"}
            onValueChange={(v) => setFilterAreaId(v === "_all" ? "" : v)}
          >
            <SelectTrigger><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Todas</SelectItem>
              {areas.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="md:ml-auto flex items-center justify-between md:justify-end gap-2 w-full md:w-auto">
          <Button variant="outline" size="sm" onClick={goPrev} aria-label="Mes anterior">
            <ChevronLeft className="size-4" />
          </Button>
          <div className="flex-1 md:flex-none md:min-w-[180px] text-center font-semibold text-slate-800">
            {MONTH_LABELS[month]} {year}
          </div>
          <Button variant="outline" size="sm" onClick={goNext} aria-label="Mes siguiente">
            <ChevronRight className="size-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday}>Hoy</Button>
        </div>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
      )}

      <Card className="p-0 overflow-hidden">
        <div className="grid grid-cols-7 border-b bg-slate-50 text-[10px] md:text-xs font-medium text-slate-600">
          {WEEKDAY_LABELS.map((label) => (
            <div key={label} className="px-1 md:px-2 py-1.5 md:py-2 text-center">
              <span className="md:hidden">{label.charAt(0)}</span>
              <span className="hidden md:inline">{label}</span>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d, idx) => {
            const key = localDateKey(d);
            const inMonth = d.getMonth() === month;
            const isToday = key === todayKey;
            const dayTasks = tasksByDay.get(key) ?? [];
            const isSelected = selectedDayKey === key;
            const hasOverdue = dayTasks.some((t) => isOverdue(t));
            const priorityDots = Array.from(
              new Set(dayTasks.map((t) => intToPriority(t.priority))),
            );

            // Mobile: tap day -> toggle expanded panel below.
            // Desktop: tap event chip -> open complete dialog.
            return (
              <button
                key={idx}
                type="button"
                onClick={() => setSelectedDayKey(isSelected ? null : key)}
                aria-pressed={isSelected}
                className={`relative text-left aspect-square md:aspect-auto md:min-h-[110px] border-r border-b p-1 md:p-1.5 flex flex-col gap-1 transition-colors ${
                  inMonth ? "bg-white" : "bg-slate-50/60"
                } ${idx % 7 === 6 ? "border-r-0" : ""} ${
                  isSelected ? "ring-2 ring-sky-500 ring-inset z-10" : ""
                } md:cursor-default md:hover:bg-slate-50`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`text-[11px] md:text-xs font-medium flex items-center justify-center ${
                      isToday
                        ? "bg-sky-600 text-white rounded-full w-5 h-5 md:w-6 md:h-6"
                        : inMonth ? "text-slate-700" : "text-slate-400"
                    }`}
                  >
                    {d.getDate()}
                  </span>
                  {dayTasks.length > 0 && (
                    <span className="hidden md:inline text-[10px] text-slate-400">{dayTasks.length}</span>
                  )}
                </div>

                {/* Mobile: dots + count */}
                <div className="md:hidden flex-1 flex flex-col items-center justify-end gap-0.5 pb-0.5">
                  {dayTasks.length > 0 && (
                    <>
                      <div className="flex gap-0.5">
                        {priorityDots.slice(0, 3).map((p) => (
                          <span
                            key={p}
                            className={`w-1.5 h-1.5 rounded-full ${
                              hasOverdue && p === intToPriority(dayTasks.find((t) => isOverdue(t))!.priority)
                                ? "bg-red-500"
                                : p === "Critical" ? "bg-red-500"
                                : p === "High" ? "bg-orange-500"
                                : p === "Normal" ? "bg-sky-500"
                                : "bg-slate-400"
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-[9px] leading-none text-slate-500">{dayTasks.length}</span>
                    </>
                  )}
                </div>

                {/* Desktop: full event chips */}
                <div className="hidden md:flex flex-col gap-1">
                  {dayTasks.map((t, i) => {
                    const overdue = isOverdue(t);
                    const priorityKey = intToPriority(t.priority);
                    const colorClass = overdue
                      ? "bg-red-100 text-red-800 border-red-300"
                      : PRIORITY_COLORS[priorityKey] ?? PRIORITY_COLORS.Normal;
                    return (
                      <span
                        key={`${t.id}-${i}`}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); setCompleting(t); }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault(); e.stopPropagation(); setCompleting(t);
                          }
                        }}
                        title={`${t.title} · ${t.equipmentName} · ${t.areaName}`}
                        className={`text-left text-[11px] leading-tight px-1.5 py-1 rounded border truncate hover:opacity-80 cursor-pointer ${colorClass}`}
                      >
                        <span className="font-medium truncate block">{t.title}</span>
                        <span className="opacity-75 truncate block">{t.equipmentName}</span>
                      </span>
                    );
                  })}
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Mobile-only expanded day panel */}
      {selectedDayKey && (() => {
        const dayTasks = tasksByDay.get(selectedDayKey) ?? [];
        const [y, m, d] = selectedDayKey.split("-").map(Number);
        const labelDate = new Date(y, m - 1, d);
        return (
          <Card className="md:hidden p-3 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">
                {labelDate.toLocaleDateString("es-ES", {
                  weekday: "long", day: "2-digit", month: "long",
                })}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedDayKey(null)}>
                Cerrar
              </Button>
            </div>
            {dayTasks.length === 0 ? (
              <div className="text-sm text-slate-500 py-2">Sin tareas este día.</div>
            ) : (
              <div className="space-y-2">
                {dayTasks.map((t, i) => {
                  const overdue = isOverdue(t);
                  return (
                    <div
                      key={`${t.id}-${i}`}
                      className={`rounded border p-3 ${
                        overdue ? "border-red-300 bg-red-50/60" : "border-slate-200 bg-white"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {overdue && <AlertCircle className="size-4 text-red-600 mt-0.5 shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate">{t.title}</div>
                          <div className="text-xs text-slate-600 truncate">
                            {t.equipmentName} · {t.areaName}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-1">
                            {CATEGORY_LABELS[intToCategory(t.category)]} ·{" "}
                            {PRIORITY_LABELS[intToPriority(t.priority)]}
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="w-full mt-3"
                        onClick={() => setCompleting(t)}
                      >
                        <CheckCircle2 className="size-4 mr-1.5" /> Marcar realizada
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        );
      })()}

      {loading && <div className="text-sm text-slate-500">Cargando…</div>}

      {overdueTasks.length > 0 && (
        <Card className="p-4 border-red-200 bg-red-50/40">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="size-4 text-red-600" />
            <h3 className="font-semibold text-red-800">
              Tareas vencidas ({overdueTasks.length})
            </h3>
          </div>
          <div className="space-y-2">
            {overdueTasks.map((t) => (
              <div
                key={t.id}
                className="flex flex-col md:flex-row md:flex-wrap gap-2 md:gap-3 md:items-center md:justify-between bg-white rounded border border-red-200 px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{t.title}</div>
                  <div className="text-xs text-slate-600 truncate">
                    {t.equipmentName} · {t.areaName} ·{" "}
                    {CATEGORY_LABELS[intToCategory(t.category)]} ·{" "}
                    {PRIORITY_LABELS[intToPriority(t.priority)]}
                  </div>
                </div>
                <div className="md:text-right">
                  <div className="text-xs text-red-700">
                    Vencida el {formatDate(t.nextDueAt, false)}
                  </div>
                </div>
                <Button size="sm" className="w-full md:w-auto" onClick={() => setCompleting(t)}>
                  <CheckCircle2 className="size-4 mr-1.5" /> Marcar realizada
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {completing && (
        <CompleteTaskDialog
          task={completing}
          onClose={() => setCompleting(null)}
          onSaved={() => { setCompleting(null); void reload(); }}
        />
      )}
    </div>
  );
};
